import React from "react";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../ui/MultiSelectDropdown";
import SortableAttributeChips from "../ui/SortableAttributeChips";

/**
 * Shared settings-panel field for a tile's configurable tooltip: an ordered
 * list of object attributes to show, plus an optional object-type override.
 * Every tile computes `attributeOptions` itself (scoping strategy differs
 * per tile — this field intentionally does not fetch or derive options).
 *
 * @param {string[]} attributeOptions
 * @param {string[]} selectedAttributes
 * @param {(next: string[]) => void} onSelectedAttributesChange
 * @param {"hover"|"click"|"hoverOrClick"} trigger - which interaction reveals the tooltip; selects the description copy
 * @param {{value: string, options: string[], onChange: (v: string) => void, baseType: string} | null} [objectTypeOverride]
 *   `baseType` is the tile's own object type — used to build the "same as
 *   above (X)" fallback option label; pass "" if none is selected yet.
 * @param {boolean} [disabled]
 */
const TRIGGER_TEXT = {
  hover: { en: "hovering", de: "Hovern" },
  click: { en: "clicking", de: "Klicken" },
  hoverOrClick: { en: "hovering or clicking", de: "Hovern oder Klicken" },
};

const TooltipAttributeSettingsField = ({
  attributeOptions = [],
  selectedAttributes = [],
  onSelectedAttributesChange,
  trigger = "hover",
  objectTypeOverride = null,
  disabled = false,
  // false when the caller already wraps this field in its own bordered/shaded
  // box (e.g. Resource Utilization Chart's Settings) — omits the redundant
  // top divider used when this field sits inline among other flat sections.
  showTopDivider = true,
}) => {
  const { t, i18n } = useTranslation("tiles");
  const lang = i18n.language?.startsWith("de") ? "de" : "en";
  const triggerText = (TRIGGER_TEXT[trigger] || TRIGGER_TEXT.hover)[lang];

  return (
    <div>
      <div className={showTopDivider ? "border-t border-gray-100 pt-4" : ""}>
        <h4 className="text-sm font-semibold text-gray-800">{t("tooltipConfig.title")}</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          {t("tooltipConfig.description", { trigger: triggerText })}
        </p>
      </div>

      {objectTypeOverride && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t("tooltipConfig.objectTypeLabel")}
          </label>
          <select
            value={objectTypeOverride.value}
            onChange={(e) => objectTypeOverride.onChange(e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">
              {t("tooltipConfig.objectTypeFallback", {
                type: objectTypeOverride.baseType || t("tooltipConfig.objectTypeNoneSelected"),
              })}
            </option>
            {objectTypeOverride.options.map((ot) => (
              <option key={ot} value={ot}>{ot}</option>
            ))}
          </select>
          {objectTypeOverride.hint && (
            <p className="text-xs text-gray-500 mt-1">{objectTypeOverride.hint}</p>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2">
        <MultiSelectDropdown
          options={attributeOptions}
          selectedValues={selectedAttributes}
          onSelectionChange={onSelectedAttributesChange}
          placeholder={t("tooltipConfig.placeholder")}
          itemType={t("tooltipConfig.itemType")}
          disabled={disabled || attributeOptions.length === 0}
        />
        <SortableAttributeChips
          attrs={selectedAttributes}
          onReorder={onSelectedAttributesChange}
          onRemove={(attr) => onSelectedAttributesChange(selectedAttributes.filter((a) => a !== attr))}
        />
      </div>
    </div>
  );
};

export default TooltipAttributeSettingsField;
